import { Fragment, type ReactNode } from 'react'

// Tiny dependency-free Markdown renderer for the AI-formatted JD (constrained output:
// ## / ### headings, - / * bullets, 1. numbered lists, **bold**, plain paragraphs).
// Renders to React elements (no dangerouslySetInnerHTML) so it's injection-safe.

function inline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = []
  const re = /\*\*([^*]+)\*\*/g
  let last = 0
  let m: RegExpExecArray | null
  let i = 0
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(<Fragment key={`${keyBase}-t${i}`}>{text.slice(last, m.index)}</Fragment>)
    out.push(<strong key={`${keyBase}-b${i}`} className="font-semibold text-foreground">{m[1]}</strong>)
    last = m.index + m[0].length
    i++
  }
  if (last < text.length) out.push(<Fragment key={`${keyBase}-t${i}`}>{text.slice(last)}</Fragment>)
  return out
}

export function Markdown({ source, className }: { source: string; className?: string }) {
  const lines = source.replace(/\r\n/g, '\n').split('\n')
  const blocks: ReactNode[] = []
  let list: { ordered: boolean; items: string[] } | null = null
  let para: string[] = []
  let k = 0

  const flushPara = () => {
    if (para.length) {
      blocks.push(<p key={`p${k++}`} className="text-sm leading-relaxed text-muted-foreground">{inline(para.join(' '), `p${k}`)}</p>)
      para = []
    }
  }
  const flushList = () => {
    if (list) {
      const items = list.items
      const ordered = list.ordered
      blocks.push(
        ordered ? (
          <ol key={`l${k++}`} className="list-decimal space-y-1 pl-5 text-sm leading-relaxed text-muted-foreground marker:text-muted-foreground/60">
            {items.map((it, idx) => <li key={idx}>{inline(it, `l${k}-${idx}`)}</li>)}
          </ol>
        ) : (
          <ul key={`l${k++}`} className="list-disc space-y-1 pl-5 text-sm leading-relaxed text-muted-foreground marker:text-muted-foreground/60">
            {items.map((it, idx) => <li key={idx}>{inline(it, `l${k}-${idx}`)}</li>)}
          </ul>
        ),
      )
      list = null
    }
  }

  for (const raw of lines) {
    const line = raw.trimEnd()
    const h = /^(#{1,6})\s+(.*)$/.exec(line)
    const bullet = /^\s*[-*•]\s+(.*)$/.exec(line)
    const num = /^\s*\d+[.)]\s+(.*)$/.exec(line)

    if (h) {
      flushPara(); flushList()
      const level = h[1].length
      const cls = level <= 2
        ? 'mt-4 mb-1.5 text-xs font-semibold uppercase tracking-wide text-foreground first:mt-0'
        : 'mt-3 mb-1 text-sm font-semibold text-foreground'
      blocks.push(level <= 2 ? <h3 key={`h${k++}`} className={cls}>{inline(h[2], `h${k}`)}</h3> : <h4 key={`h${k++}`} className={cls}>{inline(h[2], `h${k}`)}</h4>)
    } else if (bullet) {
      flushPara()
      if (!list || list.ordered) { flushList(); list = { ordered: false, items: [] } }
      list.items.push(bullet[1])
    } else if (num) {
      flushPara()
      if (!list || !list.ordered) { flushList(); list = { ordered: true, items: [] } }
      list.items.push(num[1])
    } else if (!line.trim()) {
      flushPara(); flushList()
    } else {
      flushList()
      para.push(line.trim())
    }
  }
  flushPara(); flushList()

  return <div className={className}>{blocks}</div>
}
